import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Check, Globe, Lock, Ticket, ChevronDown, CircleAlert } from 'lucide-react';
import { subscriptionAPI, paymentAPI } from '../../services/apiService';
import { usePlan } from '../../context/PlanContext';
import { openRazorpayCheckout } from '../../utils/razorpayLoader';
import { PageLoader } from '../components/PageLoader';
import { Drawer, DrawerGroup, drawerInput } from '../components/Drawer';
import { PrimaryButton } from '../components/ui';
import { Toast } from '../components/wallet/parts';
import { ConfirmDialog } from '../components/crm/ui';

// Plans page in the Olli design (pricing cards + checkout drawer).
// Plans, coupons, GST and Razorpay are the same real APIs as before.
// Prices are the server's for this org's country (GET /api/subscription/plans → pricing per cycle,
// services/planPricing.js); a yearly plan runs 14 months (12 paid + 2 free, subscriptionService);
// a coupon's discount is the server's figure in the charge currency (validate-coupon → discount).
// A paid plan's wallet credit (plans → wallet_credit { amount, currency }, null for none) is added to the
// WhatsApp / AI wallet on every payment, in the currency the server names: INR for India, USD otherwise.
// Moving a paid clinic to the free plan asks first: it takes effect at once.
// "My subscription" (/subscription/manage) is linked from the header.

const CYCLES = [
    { id: 'monthly', label: 'Monthly', months: 1, billed: '' },
    { id: 'quarterly', label: 'Quarterly', months: 3, billed: 'a quarter' },
    { id: 'annual', label: 'Annual', months: 12, billed: 'a year' },
];

const ENTERPRISE = [
    'Unlimited locations and seats',
    'Everything in Growth',
    'Custom AI agents per department',
    'Sync API and custom integrations',
    'Single sign-on and role-based access',
    'Dedicated success manager',
    'Onboarding for every branch',
];

const PlansPage = () => {
    const [plans, setPlans] = useState([]);
    const [loading, setLoading] = useState(true);
    const [billingCycle, setBillingCycle] = useState('monthly');
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const navigate = useNavigate();
    const plan = usePlan();
    const currentSub = plan?.getSubscription();

    // Checkout drawer state
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [selectedPlan, setSelectedPlan] = useState(null);
    const [couponCode, setCouponCode] = useState('');
    const [couponResult, setCouponResult] = useState(null);
    const [validatingCoupon, setValidatingCoupon] = useState(false);
    const [availableCoupons, setAvailableCoupons] = useState([]);
    const [loadingCoupons, setLoadingCoupons] = useState(false);
    const [showCouponList, setShowCouponList] = useState(false);
    const [subscribing, setSubscribing] = useState(false);
    const [drawerError, setDrawerError] = useState('');
    const [gstInfo, setGstInfo] = useState(null); // { gstRate, isIndian }
    const [currency, setCurrency] = useState('usd'); // what this org is charged in: 'inr', 'usd', 'aed', 'gbp'…
    const [freeConfirm, setFreeConfirm] = useState(null); // the free plan, while asking a paying clinic to confirm

    useEffect(() => {
        fetchPlans();
        subscriptionAPI.getCurrency()
            .then(r => { if (r.data.success) setCurrency(r.data.data.currency); })
            .catch(() => {});
    }, []);

    const fetchPlans = async () => {
        try {
            const r = await subscriptionAPI.getPlans();
            if (r.data.success) setPlans(r.data.data || []);
        } catch (err) { setError('Failed to load plans'); }
        finally { setLoading(false); }
    };

    const fetchAvailableCoupons = useCallback(async (planId) => {
        setLoadingCoupons(true);
        try {
            const r = await subscriptionAPI.getAvailableCoupons({ plan_id: planId, billing_cycle: billingCycle });
            if (r.data.success) setAvailableCoupons(r.data.data || []);
        } catch { setAvailableCoupons([]); }
        finally { setLoadingCoupons(false); }
    }, [billingCycle]);

    // Prices for this org's country, from the server (services/planPricing.js):
    // India in INR, a few countries in their own currency, everyone else USD.
    // Plans from an older server have no `pricing` and use the plan columns.
    const chargeCode = (plans.find((p) => p.pricing)?.pricing.currency || (currency === 'inr' ? 'INR' : currency === 'usd' ? 'USD' : String(currency).toUpperCase())).toUpperCase();
    const local = (p, cycleId = billingCycle) => p.pricing?.[cycleId] || null;

    const getPrice = (p) => {
        const l = local(p);
        if (l) {
            const regular = Number(l.regular_per_month) || 0;
            const perMonth = Number(l.per_month) || 0;
            return { regular, sale: perMonth && perMonth < regular ? perMonth : null };
        }
        const suffix = currency === 'inr' ? 'inr' : 'usd';
        const regular = billingCycle === 'quarterly' ? p[`price_quarterly_${suffix}`] : billingCycle === 'annual' ? p[`price_annual_${suffix}`] : p[`price_monthly_${suffix}`];
        const sale = billingCycle === 'quarterly' ? p[`sale_price_quarterly_${suffix}`] : billingCycle === 'annual' ? p[`sale_price_annual_${suffix}`] : p[`sale_price_monthly_${suffix}`];
        return { regular: parseFloat(regular) || 0, sale: sale ? parseFloat(sale) : null };
    };

    const months = CYCLES.find(c => c.id === billingCycle).months;
    const cycle = CYCLES.find(c => c.id === billingCycle);

    const getTotalPrice = (p) => {
        if (local(p)) return Number(local(p).total) || 0;
        const { regular, sale } = getPrice(p);
        return (sale || regular) * months;
    };

    const formatPrice = (amount) => {
        if (!amount) return 'Free';
        const fraction = Number.isInteger(Math.round(amount * 100) / 100) ? 0 : 2;
        return new Intl.NumberFormat(chargeCode === 'INR' ? 'en-IN' : 'en-US', { style: 'currency', currency: chargeCode, minimumFractionDigits: fraction, maximumFractionDigits: fraction }).format(amount);
    };

    // The plan's wallet credit, as the server gives it: its own amount and currency, not the charge currency.
    const creditOf = (p) => {
        const c = p?.wallet_credit;
        if (!c || !(Number(c.amount) > 0)) return null;
        const code = String(c.currency || 'USD').toUpperCase();
        return new Intl.NumberFormat(code === 'INR' ? 'en-IN' : 'en-US', { style: 'currency', currency: code, maximumFractionDigits: Number.isInteger(Number(c.amount)) ? 0 : 2 }).format(Number(c.amount));
    };

    // Coupons are defined in rupees (flat amounts) and rupees or dollars (caps),
    // whatever the charge currency, so their own amounts are shown in those.
    const inr = (n) => new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(n);
    const usd = (n) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(n);

    const getDiscountedTotal = () => {
        if (!selectedPlan) return 0;
        let total = getTotalPrice(selectedPlan);
        if (couponResult?.valid && couponResult.discount) {
            // The server's figure, worked out exactly as checkout will.
            return Math.max(0, total - (Number(couponResult.discount.amount) || 0));
        }
        if (couponResult?.valid) {
            const c = couponResult.coupon;
            let discount = 0;
            if (c.discount_type === 'percentage') {
                discount = (total * c.discount_value) / 100;
                const maxCap = currency === 'inr' ? c.max_discount_inr : c.max_discount_usd;
                if (maxCap && discount > maxCap) discount = maxCap;
            } else {
                discount = c.discount_value;
            }
            total = Math.max(0, total - discount);
        }
        return total;
    };

    const getDiscountAmount = () => {
        if (!selectedPlan || !couponResult?.valid) return 0;
        return getTotalPrice(selectedPlan) - getDiscountedTotal();
    };

    const openDrawer = async (p) => {
        setSelectedPlan(p);
        setCouponCode('');
        setCouponResult(null);
        setShowCouponList(false);
        setDrawerError('');
        setDrawerOpen(true);
        fetchAvailableCoupons(p.id);
        // Fetch GST info for this org
        try {
            const r = await paymentAPI.calculateGst(1); // amount doesn't matter, we just need rate + isIndian
            if (r.data.success) {
                setGstInfo({ gstRate: r.data.calculation.gstRate, isIndian: r.data.calculation.isIndian });
            }
        } catch { setGstInfo(null); }
    };

    const closeDrawer = () => {
        setDrawerOpen(false);
        setTimeout(() => {
            setSelectedPlan(null);
            setCouponCode('');
            setCouponResult(null);
            setAvailableCoupons([]);
            setShowCouponList(false);
            setDrawerError('');
        }, 300);
    };

    const handleValidateCoupon = async (code) => {
        const codeToUse = code || couponCode;
        if (!codeToUse.trim()) return;
        setValidatingCoupon(true);
        setCouponResult(null);
        try {
            const r = await subscriptionAPI.validateCoupon(codeToUse, selectedPlan?.id, billingCycle);
            setCouponResult(r.data);
            if (r.data.valid) setCouponCode(codeToUse.toUpperCase());
        } catch (err) {
            setCouponResult({ valid: false, message: err.response?.data?.message || 'Invalid coupon' });
        } finally { setValidatingCoupon(false); }
    };

    const applyCouponFromList = (coupon) => {
        setCouponCode(coupon.code);
        handleValidateCoupon(coupon.code);
        setShowCouponList(false);
    };

    const removeCoupon = () => {
        setCouponCode('');
        setCouponResult(null);
    };

    const handleSubscribe = async () => {
        if (!selectedPlan) return;
        setSubscribing(true);
        setDrawerError('');
        try {
            const r = await subscriptionAPI.subscribe({
                plan_id: selectedPlan.id,
                billing_cycle: billingCycle,
                coupon_code: couponResult?.valid ? couponCode : undefined,
            });
            if (r.data.success) {
                if (r.data.data?.requires_payment && r.data.data?.razorpayOrderId) {
                    payWithRazorpay(r.data.data);
                } else {
                    setSuccess('Subscription activated!');
                    plan?.refresh();
                    closeDrawer();
                    setTimeout(() => navigate('/dashboard'), 2000);
                }
            }
        } catch (err) {
            setDrawerError(err.response?.data?.message || 'Failed to subscribe');
        } finally { setSubscribing(false); }
    };

    /**
     * Open Razorpay Checkout for a plan, then have the server verify it.
     *
     * The plan is activated by the server, not here — this only carries the
     * signature back. If it never gets the chance to (tab closed mid-payment),
     * Razorpay's webhook activates the plan instead, so a customer who paid is
     * never left on their old plan because their browser gave up.
     */
    const payWithRazorpay = async (order) => {
        let response;
        try {
            response = await openRazorpayCheckout(order, {
                name: 'Olli',
                description: `${order.planName || 'Subscription'} — ${billingCycle}`,
            });
        } catch (e) {
            setDrawerError(e.dismissed ? 'Payment cancelled' : (e.message || 'Payment failed'));
            return;
        }

        try {
            const verifyResult = await subscriptionAPI.verifyPayment({
                razorpay_order_id: response.razorpay_order_id,
                razorpay_payment_id: response.razorpay_payment_id,
                razorpay_signature: response.razorpay_signature,
            });
            if (verifyResult.data.success) {
                setSuccess('Payment successful! Subscription activated.');
                plan?.refresh();
                closeDrawer();
                setTimeout(() => navigate('/dashboard'), 2000);
            } else {
                setDrawerError(verifyResult.data.message || 'We could not verify the payment. Contact support.');
            }
        } catch (err) {
            // The payment may well have gone through — the webhook settles it —
            // so this does not claim the money was lost.
            setDrawerError(err.response?.data?.message
                || 'Payment taken but not yet confirmed. It will appear shortly; contact support if it does not.');
        }
    };

    // Free plan subscription directly (no drawer needed)
    const handleFreeSubscribe = async (planId) => {
        setError('');
        try {
            const r = await subscriptionAPI.subscribe({ plan_id: planId, billing_cycle: 'monthly' });
            if (r.data.success) {
                setSuccess('Free plan activated!');
                plan?.refresh();
                setTimeout(() => navigate('/dashboard'), 2000);
            }
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to activate plan');
        }
    };

    if (loading) return <div className="olli font-sans"><PageLoader label="Loading plans…" /></div>;

    // GST is Indian tax: only on INR prices.
    const gstRate = chargeCode === 'INR' ? (gstInfo?.gstRate || 0) : 0;
    const subtotal = getDiscountedTotal();
    const gstAmount = gstRate > 0 ? Math.round(subtotal * (gstRate / 100) * 100) / 100 : 0;
    const totalPayable = Math.round((subtotal + gstAmount) * 100) / 100;

    return (
        <div className="olli relative mx-auto flex max-w-7xl flex-col gap-8 px-8 py-9 font-sans text-ink">
            {/* Header */}
            <header className="flex animate-fade-in flex-col items-center gap-3 text-center">
                <h1 className="text-h1 font-semibold">One price. No night shift.</h1>
                <p className="max-w-xl text-body text-muted">
                    Start free and scale as you grow. All plans include CRM, patients, appointments, and treatment management.
                </p>

                {/* Billing toggle */}
                <div role="radiogroup" aria-label="Billing cycle" className="mt-3 inline-flex items-center gap-1 rounded-full bg-paper p-1.5 shadow-soft">
                    {CYCLES.map(c => {
                        // Largest saving across the paid plans for this cycle
                        const cycleSavings = (() => {
                            if (c.id === 'monthly') return 0;
                            const paidPlans = plans.filter(p => !p.is_free);
                            if (paidPlans.length === 0) return 0;
                            const savings = paidPlans.map(p => {
                                if (p.pricing) {
                                    const m = Number(p.pricing.monthly?.per_month) || 0;
                                    const cp = Number(p.pricing[c.id]?.per_month) || 0;
                                    return m && cp && cp < m ? Math.round(((m - cp) / m) * 100) : 0;
                                }
                                const monthly = parseFloat(p.sale_price_monthly_usd || p.price_monthly_usd) || 0;
                                const cyclePrice = c.id === 'quarterly'
                                    ? parseFloat(p.sale_price_quarterly_usd || p.price_quarterly_usd) || 0
                                    : parseFloat(p.sale_price_annual_usd || p.price_annual_usd) || 0;
                                if (!monthly || !cyclePrice || cyclePrice >= monthly) return 0;
                                return Math.round(((monthly - cyclePrice) / monthly) * 100);
                            });
                            return Math.max(...savings, 0);
                        })();
                        const on = billingCycle === c.id;
                        return (
                            <button
                                key={c.id}
                                type="button"
                                role="radio"
                                aria-checked={on}
                                onClick={() => { setBillingCycle(c.id); setCouponResult(null); }}
                                className={`flex h-10 items-center gap-2 whitespace-nowrap rounded-full px-3.5 text-small sm:px-5 font-semibold transition-colors ${on ? 'bg-ink text-paper' : 'text-muted hover:text-ink'}`}
                            >
                                {c.label}
                                {c.id === 'annual' && (
                                    <span className="hidden rounded-full bg-sun px-2 py-0.5 text-[11px] font-semibold text-ink sm:inline">+2 months</span>
                                )}
                                {cycleSavings > 0 && (
                                    <span className="rounded-full bg-sun px-2 py-0.5 text-[11px] font-semibold text-ink">{cycleSavings}% off</span>
                                )}
                            </button>
                        );
                    })}
                </div>
                <p className="flex flex-wrap items-center justify-center gap-x-3 gap-y-1 text-caption text-muted">
                    <span className="flex items-center gap-1.5"><Globe size={13} strokeWidth={1.75} />Prices in {chargeCode === 'INR' ? 'INR for India, plus GST' : chargeCode}</span>
                    {billingCycle === 'annual' && <span>Yearly plans run 14 months: 12 paid, 2 free</span>}
                    {currentSub && <Link to="/subscription/manage" className="font-semibold text-sky-deep hover:underline">My subscription</Link>}
                </p>
            </header>

            {error && <p role="alert" className="mx-auto max-w-lg rounded-md bg-danger-soft px-4 py-3 text-center text-small font-medium text-danger">{error}</p>}

            {/* Plans */}
            <div className={`mx-auto grid w-full grid-cols-1 items-stretch gap-5 md:grid-cols-2 ${plans.length >= 3 ? 'max-w-7xl xl:grid-cols-4' : plans.length === 2 ? 'max-w-6xl xl:grid-cols-3' : 'max-w-4xl xl:grid-cols-2'}`}>
                {plans.map((p, i) => {
                    const { regular, sale } = getPrice(p);
                    const effectivePrice = sale || regular;
                    const isCurrent = currentSub?.plan_id === p.id;
                    const isFree = !!p.is_free;
                    const isPopular = !!p.is_popular;
                    const features = (() => { try { return typeof p.features_json === 'string' ? JSON.parse(p.features_json) : (p.features_json || []); } catch { return []; } })();
                    const savings = sale && regular > sale ? Math.round(((regular - sale) / regular) * 100) : 0;
                    const dark = isPopular;

                    return (
                        <article
                            key={p.id}
                            style={{ animationDelay: `${60 + i * 60}ms` }}
                            className={`relative flex animate-nav-in flex-col rounded-xl p-7 ${dark ? 'bg-ink bg-[image:var(--gradient-night)] text-paper shadow-lift' : 'bg-paper shadow-soft'} ${isCurrent ? 'ring-2 ring-sun' : ''}`}
                        >
                            <div className="flex flex-wrap items-start justify-between gap-x-3 gap-y-2">
                                <h2 className="text-h3 font-semibold">{p.name}</h2>
                                {isCurrent ? (
                                    <span className={`whitespace-nowrap rounded-full px-3 py-1 text-[11px] font-semibold ${dark ? 'bg-sun text-ink' : 'bg-success-soft text-success'}`}>Current plan</span>
                                ) : isPopular ? (
                                    <span className="whitespace-nowrap rounded-full bg-sun px-3 py-1 text-[11px] font-semibold text-ink">Built for busy clinics</span>
                                ) : null}
                            </div>
                            <p className={`mt-1.5 min-h-[40px] text-small ${dark ? 'text-paper/70' : 'text-muted'}`}>{p.description}</p>

                            {/* Price */}
                            <div className="mt-6">
                                {isFree ? (
                                    <p className="flex items-baseline gap-2">
                                        <span className="text-[40px] font-semibold leading-none tracking-tight">Free</span>
                                        <span className={`text-small ${dark ? 'text-paper/70' : 'text-muted'}`}>forever</span>
                                    </p>
                                ) : (
                                    <>
                                        <div className="flex h-5 items-center gap-2">
                                            {sale && <span className={`text-small line-through ${dark ? 'text-paper/55' : 'text-muted'}`}>{formatPrice(regular)}</span>}
                                            {savings > 0 && <span className="rounded-full bg-sun px-2 py-0.5 text-[11px] font-semibold text-ink">{savings}% off</span>}
                                        </div>
                                        {/* Long local prices (AED 1,469, SAR 1,499) step down a size, and
                                            "/ month" drops to the next line rather than leaving the card. */}
                                        <p className="mt-1 flex flex-wrap items-baseline gap-x-2 gap-y-1">
                                            <span className={`${formatPrice(effectivePrice).length >= 9 ? 'text-[30px]' : formatPrice(effectivePrice).length >= 7 ? 'text-[34px]' : 'text-[40px]'} whitespace-nowrap font-semibold leading-none tracking-tight tabular-nums`}>{formatPrice(effectivePrice)}</span>
                                            <span className={`text-small ${dark ? 'text-paper/70' : 'text-muted'}`}>/ month</span>
                                        </p>
                                        <p className={`mt-2 min-h-[16px] text-caption ${dark ? 'text-paper/60' : 'text-muted'}`}>
                                            {cycle.billed ? `Billed ${formatPrice(getTotalPrice(p))} ${cycle.billed}${billingCycle === 'annual' ? ' · 2 months free (14 months)' : ''}` : ''}
                                        </p>
                                        {creditOf(p) && (
                                            <p className={`mt-3 w-fit rounded-md px-3 py-1.5 text-caption font-semibold ${dark ? 'bg-paper/15 text-paper' : 'bg-success-soft text-success'}`}>
                                                + {creditOf(p)} wallet credit with every payment
                                            </p>
                                        )}
                                    </>
                                )}
                            </div>

                            {/* CTA */}
                            {isCurrent ? (
                                <button
                                    type="button"
                                    disabled
                                    className={`mt-6 h-12 w-full cursor-not-allowed rounded-full text-small font-semibold ${dark ? 'bg-paper/15 text-paper/60' : 'bg-mist text-muted'}`}
                                >
                                    Current plan
                                </button>
                            ) : (
                                <button
                                    type="button"
                                    onClick={() => (isFree ? (currentSub && !currentSub.is_free ? setFreeConfirm(p) : handleFreeSubscribe(p.id)) : openDrawer(p))}
                                    className={`mt-6 h-12 w-full rounded-full text-body font-semibold hover:brightness-95 ${dark ? 'bg-sun text-ink' : isFree ? 'bg-paper text-ink ring-1 ring-line hover:bg-mist' : 'bg-ink text-paper hover:bg-ink/85'}`}
                                >
                                    {isFree ? 'Get started free' : 'Upgrade now'}
                                </button>
                            )}

                            {/* Features */}
                            <ul className={`mt-6 flex flex-1 flex-col gap-3 border-t pt-6 ${dark ? 'border-paper/15' : 'border-line'}`}>
                                {features.map((f, k) => (
                                    <li key={k} className="flex items-start gap-3 text-small">
                                        <span className={`mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center ${dark ? 'text-sun' : 'text-success'}`}>
                                            <Check size={15} strokeWidth={2.25} />
                                        </span>
                                        {f}
                                    </li>
                                ))}
                            </ul>
                        </article>
                    );
                })}

                {/* Enterprise */}
                <article
                    style={{ animationDelay: `${60 + plans.length * 60}ms` }}
                    className="relative flex animate-nav-in flex-col rounded-xl bg-paper p-7 shadow-soft"
                >
                    <h2 className="text-h3 font-semibold">Enterprise</h2>
                    <p className="mt-1.5 min-h-[40px] text-small text-muted">For hospitals and groups with many front desks</p>
                    <div className="mt-6">
                        <div className="h-5" />
                        <p className="mt-1 text-[40px] font-semibold leading-none tracking-tight">Custom</p>
                        <p className="mt-2 text-caption text-muted">Priced around your branches and departments</p>
                    </div>
                    <a
                        href="mailto:support@getolliai.com?subject=Olli%20Enterprise"
                        className="mt-6 flex h-12 w-full items-center justify-center rounded-full bg-paper text-small font-semibold text-ink ring-1 ring-line hover:bg-mist"
                    >
                        Talk to sales
                    </a>
                    <ul className="mt-6 flex flex-1 flex-col gap-3 border-t border-line pt-6">
                        {ENTERPRISE.map((f) => (
                            <li key={f} className="flex items-start gap-3 text-small">
                                <span className="mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center text-success">
                                    <Check size={15} strokeWidth={2.25} />
                                </span>
                                {f}
                            </li>
                        ))}
                    </ul>
                </article>
            </div>

            {/* Checkout drawer */}
            <Drawer
                open={drawerOpen}
                onClose={closeDrawer}
                context="Checkout"
                title="Order summary"
                actions={
                    <PrimaryButton
                        type="button"
                        onClick={handleSubscribe}
                        loading={subscribing}
                        loadingLabel="Processing…"
                        className="h-11 px-6 text-small"
                    >
                        {`Pay ${formatPrice(totalPayable)}`}
                    </PrimaryButton>
                }
            >
                {selectedPlan && (
                    <>
                        {/* Selected plan */}
                        <DrawerGroup>
                            <div className="rounded-md bg-mist p-5">
                                <div className="flex items-start justify-between gap-3">
                                    <div>
                                        <p className="text-caption font-semibold uppercase tracking-wider text-muted">Selected plan</p>
                                        <h3 className="mt-1 text-h3 font-semibold">{selectedPlan.name}</h3>
                                    </div>
                                    <span className="rounded-full bg-ink px-3 py-1 text-caption font-semibold text-paper">{cycle.label}{billingCycle === 'annual' ? ' · 14 months' : ''}</span>
                                </div>
                                {selectedPlan.description && <p className="mt-2 text-small text-muted">{selectedPlan.description}</p>}
                            </div>
                        </DrawerGroup>

                        {/* Price breakdown */}
                        <DrawerGroup title="Price breakdown">
                            <dl className="flex flex-col gap-2.5 text-small">
                                <div className="flex justify-between gap-3">
                                    <dt className="text-muted">{formatPrice(getTotalPrice(selectedPlan) / months)} × {months} {months === 1 ? 'month' : 'months'}</dt>
                                    <dd className="font-semibold tabular-nums">{formatPrice(getTotalPrice(selectedPlan))}</dd>
                                </div>
                                {couponResult?.valid && (
                                    <div className="flex justify-between gap-3 text-success">
                                        <dt className="flex items-center gap-1.5"><Ticket size={14} strokeWidth={1.75} />Coupon ({couponCode})</dt>
                                        <dd className="font-semibold tabular-nums">−{formatPrice(getDiscountAmount())}</dd>
                                    </div>
                                )}
                                <div className="flex justify-between gap-3">
                                    <dt className="text-muted">Subtotal</dt>
                                    <dd className="font-semibold tabular-nums">{formatPrice(subtotal)}</dd>
                                </div>
                                {gstRate > 0 && (
                                    <div className="flex justify-between gap-3">
                                        <dt className="text-muted">GST ({gstRate}%)</dt>
                                        <dd className="font-semibold tabular-nums">{formatPrice(gstAmount)}</dd>
                                    </div>
                                )}
                                <div className="mt-1 flex justify-between gap-3 border-t border-line pt-3 text-body">
                                    <dt className="font-semibold">Total payable</dt>
                                    <dd className="font-semibold tabular-nums">{formatPrice(totalPayable)}</dd>
                                </div>
                            </dl>
                            {creditOf(selectedPlan) && (
                                <p className="mt-4 rounded-md bg-success-soft px-4 py-3 text-small text-success">
                                    <span className="font-semibold">{creditOf(selectedPlan)} wallet credit</span> is added to your wallet once this payment goes through, for WhatsApp messages and AI. It isn't taken off the price.
                                </p>
                            )}
                        </DrawerGroup>

                        {/* Coupon */}
                        <DrawerGroup title="Apply coupon">
                            {couponResult?.valid ? (
                                <div className="flex items-center justify-between gap-3 rounded-md bg-success-soft px-4 py-3">
                                    <div className="flex items-center gap-3">
                                        <span className="flex h-8 w-8 items-center justify-center rounded-full bg-paper text-success"><Check size={16} strokeWidth={2.25} /></span>
                                        <div>
                                            <p className="text-small font-semibold text-success">{couponCode}</p>
                                            <p className="text-caption text-success">
                                                {couponResult.discount ? `${formatPrice(couponResult.discount.amount)} off` : couponResult.coupon.discount_type === 'percentage' ? `${couponResult.coupon.discount_value}% off` : `${inr(couponResult.coupon.discount_value)} off`} applied
                                            </p>
                                        </div>
                                    </div>
                                    <button type="button" onClick={removeCoupon} className="rounded-full px-3 py-1.5 text-small font-semibold text-danger hover:bg-paper/70">Remove</button>
                                </div>
                            ) : (
                                <div className="flex flex-col gap-2">
                                    <div className="flex gap-2">
                                        <input
                                            type="text"
                                            placeholder="Enter coupon code"
                                            value={couponCode}
                                            aria-invalid={couponResult && !couponResult.valid ? 'true' : undefined}
                                            onChange={(e) => { setCouponCode(e.target.value.toUpperCase()); setCouponResult(null); }}
                                            onKeyDown={(e) => e.key === 'Enter' && handleValidateCoupon()}
                                            className={`${drawerInput} font-semibold tracking-wide`}
                                        />
                                        <button
                                            type="button"
                                            onClick={() => handleValidateCoupon()}
                                            disabled={validatingCoupon || !couponCode.trim()}
                                            className="h-11 shrink-0 rounded-full bg-ink px-5 text-small font-semibold text-paper hover:bg-ink/85 disabled:cursor-not-allowed disabled:bg-mist disabled:text-muted"
                                        >
                                            {validatingCoupon ? 'Checking…' : 'Apply'}
                                        </button>
                                    </div>
                                    {couponResult && !couponResult.valid && (
                                        <p role="alert" className="flex animate-fade-in items-center gap-1.5 text-caption text-danger"><CircleAlert size={14} strokeWidth={1.75} />{couponResult.message}</p>
                                    )}
                                    <button
                                        type="button"
                                        onClick={() => setShowCouponList(!showCouponList)}
                                        className="flex w-fit items-center gap-1.5 py-1 text-small font-semibold text-sky-deep"
                                    >
                                        {showCouponList ? 'Hide available coupons' : 'View available coupons'}
                                        <ChevronDown size={14} strokeWidth={2} className={`transition-transform ${showCouponList ? 'rotate-180' : ''}`} />
                                    </button>
                                </div>
                            )}

                            {showCouponList && !couponResult?.valid && (
                                loadingCoupons ? (
                                    <p className="py-4 text-center text-small text-muted">Loading coupons…</p>
                                ) : availableCoupons.length === 0 ? (
                                    <p className="rounded-md bg-mist py-4 text-center text-small text-muted">No coupons available for this plan</p>
                                ) : (
                                    <ul className="flex flex-col gap-2">
                                        {availableCoupons.map(c => (
                                            <li key={c.id} className="flex items-center justify-between gap-3 rounded-md border border-dashed border-line px-4 py-3 hover:border-muted/60">
                                                <div className="min-w-0">
                                                    <p className="text-small font-semibold tracking-wide">{c.code}</p>
                                                    <p className="text-caption text-muted">
                                                        {c.discount_type === 'percentage' ? `${c.discount_value}% off` : `${inr(c.discount_value)} off`}
                                                        {chargeCode === 'INR' ? (c.max_discount_inr ? ` (max ${inr(c.max_discount_inr)})` : '') : (c.max_discount_usd ? ` (max ${usd(c.max_discount_usd)})` : '')}
                                                    </p>
                                                    {c.description && <p className="mt-0.5 text-caption text-muted">{c.description}</p>}
                                                </div>
                                                <button
                                                    type="button"
                                                    onClick={() => applyCouponFromList(c)}
                                                    className="h-9 shrink-0 rounded-full bg-mist px-4 text-small font-semibold hover:bg-line"
                                                >
                                                    Apply
                                                </button>
                                            </li>
                                        ))}
                                    </ul>
                                )
                            )}
                        </DrawerGroup>

                        {drawerError && <p role="alert" className="mt-5 animate-fade-in rounded-md bg-danger-soft px-4 py-3 text-small font-medium text-danger">{drawerError}</p>}

                        <p className="mt-5 flex items-center justify-center gap-1.5 text-caption text-muted">
                            <Lock size={13} strokeWidth={1.75} />
                            Secured by Razorpay
                        </p>
                    </>
                )}
            </Drawer>

            {freeConfirm && <ConfirmDialog title="Move to the free plan?" body={<p>{currentSub?.plan_name || 'Your plan'} ends now and the clinic moves to {freeConfirm.name}. Anything not in the free plan stops working straight away.</p>} confirmLabel="Move to free plan" onCancel={() => setFreeConfirm(null)} onConfirm={() => { const id = freeConfirm.id; setFreeConfirm(null); handleFreeSubscribe(id); }} />}
            {success && <Toast message={success} onDone={() => setSuccess('')} />}
        </div>
    );
};

export default PlansPage;
